/*
 * The app's time zone picker: an autocomplete over the zones the browser knows.
 *
 * It replaced four text boxes that wanted a typed IANA id ("Europe/Warsaw") and checked nothing, so
 * `warsaw` and `CET` reached the form and were refused by the host after Save
 * (`docs/2026-10-05-date-time-components.md`). The zone is half of every time a draft promises a
 * lead, and a rep knows it as a city or a country, or as the "EST" a lead wrote.
 *
 * How it behaves:
 *
 * - **Type a place.** A city, a country, an abbreviation, or the id itself. Each candidate shows
 *   its offset and its clock right now, which is how two of them are told apart.
 * - **Before typing**, the zones worth offering come first, each saying why: the one the call site
 *   suggests, this browser's own, and the ones chosen here before. The whole list follows.
 * - **Keyboard throughout.** Arrows move, Enter or Tab take the highlighted zone, Escape puts back
 *   what was there. Focus never leaves the box, so typing can resume at any point.
 * - **The form is handed a zone the host accepts, or nothing.** What is stored is the IANA id, as
 *   before. A query that settles on no zone leaves the earlier value and says so; it is never
 *   handed over as it stands.
 *
 * The field takes its name from a `<label htmlFor>` at the call site. Styles: "date and time
 * fields" in `styles.css`.
 */
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  isTimeZone,
  offsetLabel,
  timeZoneNames,
  viewerTimeZone,
  zoneClock,
  zoneWords,
} from "../src/app-dates.ts";
import { fold } from "../src/date-entry.ts";
import { findZone, searchZones, type Zone, zoneCatalog, zoneOf } from "../src/time-zones.ts";
import { useHeldWhilePressed, usePopoverEdge } from "./date-time-fields.tsx";
import { CheckIcon, ChevronDownIcon } from "./icons.tsx";

/** A zone the call site already knows is likely, and the few words that say why. */
export type SuggestedZone = { readonly id: string; readonly why: string };

type ZoneStorage = Pick<Storage, "getItem" | "setItem">;

const RECENT_KEY = "leadcue-reply-recent-zones";
const RECENT_KEPT = 4;
/** More matches than this is a query to narrow, not a list to read. */
const MATCHES_SHOWN = 40;

let listed: readonly Zone[] | null = null;
/** Built on first use and kept: the browser's list does not change under a running page. */
function catalog(): readonly Zone[] {
  listed ??= zoneCatalog(timeZoneNames(), isTimeZone);
  return listed;
}

function browserStorage(): ZoneStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // A browser that blocks storage still gets a working picker, with nothing remembered.
    return null;
  }
}

/** The zones chosen in this browser before, most recent first. Anything unreadable is nothing. */
function recentZones(storage: ZoneStorage | null): readonly string[] {
  try {
    const held: unknown = JSON.parse(storage?.getItem(RECENT_KEY) ?? "[]");
    if (!Array.isArray(held)) return [];
    return held
      .filter((id): id is string => typeof id === "string" && isTimeZone(id))
      .slice(0, RECENT_KEPT);
  } catch {
    return [];
  }
}

function remember(storage: ZoneStorage | null, id: string): void {
  try {
    const next = [id, ...recentZones(storage).filter((held) => held !== id)].slice(0, RECENT_KEPT);
    storage?.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Remembering is a convenience. A full or blocked store must not stop the choice.
  }
}

/** One line of the list. A zone can be offered twice, once suggested and once in its place. */
type Row = {
  readonly zone: Zone;
  readonly group: "found" | "suggested" | "all";
  readonly why: string | null;
  readonly heading: string | null;
};

const describe = (id: string): Zone => findZone(catalog(), id) ?? zoneOf(id);

/** The zone's own name ("Central European Time"), when the runtime has one. */
const nameOf = (id: string, now: number): string | null =>
  zoneWords(id, now).find((word) => word.includes(" ")) ?? null;

/** What a zone's clock says now: "UTC+2" and "12:00". */
function clockOf(id: string, now: number): { offset: string; time: string } | null {
  const clock = zoneClock(id, now);
  return clock === null ? null : { offset: offsetLabel(clock.offsetMinutes), time: clock.time };
}

export function TimeZoneField(props: {
  /** The input's id, which the call site's `<label htmlFor>` names. */
  readonly id: string;
  /** The stored IANA id, or `""`. */
  readonly value: string;
  /** Handed an id the host accepts, or `""` when the field is emptied. */
  readonly onChange: (zone: string) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly placeholder?: string;
  /** Zones the call site knows are likely, offered first with their reason. */
  readonly suggested?: readonly SuggestedZone[];
  readonly describedBy?: string;
  /** The instant the candidates' clocks are read at. Tests pin it; the app leaves it out. */
  readonly now?: number;
  /** This browser's zone. Tests pin it; the app leaves it out. */
  readonly browserZone?: string;
  /** Where chosen zones are remembered. Tests pass one; the app uses the browser's. */
  readonly storage?: ZoneStorage | null;
}) {
  const { value, onChange, disabled } = props;
  const storage = props.storage === undefined ? browserStorage() : props.storage;
  const [started] = useState(() => Date.now());
  const now = props.now ?? started;
  const browserZone = props.browserZone ?? viewerTimeZone();

  const [text, setText] = useState(value);
  /** The box holds a query being typed, not the value. */
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(0);
  /** A query that settled on nothing, kept to say so after the box goes back to its value. */
  const [missed, setMissed] = useState<string | null>(null);
  const [recent, setRecent] = useState(() => recentZones(storage));

  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const edge = usePopoverEdge(open, root, list);
  const listId = useId();
  const noteId = useId();
  const missedId = useId();
  const optionId = (index: number): string => `${listId}-${index}`;

  // The form changing the zone itself (a proposal chosen, a reset) shows in the box. A form that
  // keeps its old value after being handed a new one does not undo what the rep just chose.
  const seen = useRef(value);
  useEffect(() => {
    if (value === seen.current) return;
    seen.current = value;
    if (!searching) setText(value);
  }, [value, searching]);

  const query = searching ? text.trim() : "";
  const suggested = props.suggested;
  const rows = useMemo((): readonly Row[] => {
    if (!open) return [];
    if (query !== "")
      return searchZones(catalog(), query, { words: (id) => zoneWords(id, now) })
        .slice(0, MATCHES_SHOWN)
        .map((zone) => ({ zone, group: "found", why: null, heading: null }));
    const offered = new Map<string, string>();
    for (const { id, why } of suggested ?? [])
      if (isTimeZone(id) && !offered.has(id)) offered.set(id, why);
    if (isTimeZone(browserZone) && !offered.has(browserZone))
      offered.set(browserZone, "Your browser");
    for (const id of recent) if (!offered.has(id)) offered.set(id, "Used before");
    return [
      ...[...offered].map(
        ([id, why], index): Row => ({
          zone: describe(id),
          group: "suggested",
          why,
          heading: index === 0 ? "Suggested" : null,
        }),
      ),
      ...catalog().map(
        (zone, index): Row => ({
          zone,
          group: "all",
          why: null,
          heading: index === 0 ? "All time zones" : null,
        }),
      ),
    ];
  }, [open, query, suggested, browserZone, recent, now]);
  const current = rows[at];

  // Keep the highlighted zone in view, by hand: scrollIntoView would move the page as well.
  useEffect(() => {
    const box = list.current;
    const option = open ? box?.querySelector<HTMLElement>('[aria-selected="true"]') : null;
    if (!box || !option) return;
    if (option.offsetTop < box.scrollTop) box.scrollTop = option.offsetTop - 8;
    else if (option.offsetTop + option.offsetHeight > box.scrollTop + box.clientHeight)
      box.scrollTop = option.offsetTop + option.offsetHeight - box.clientHeight + 8;
  });

  const rest = (): void => {
    setSearching(false);
    setOpen(false);
    setAt(0);
  };
  const choose = (id: string): void => {
    setMissed(null);
    setText(id);
    rest();
    if (id !== "") {
      remember(storage, id);
      setRecent(recentZones(storage));
    }
    if (id !== value) onChange(id);
  };
  const putBack = (): void => {
    setText(value);
    rest();
  };

  /** Leaving the box with a query in it: take what it plainly means, or put the value back. */
  const settle = (): void => {
    if (!searching) {
      setOpen(false);
      return;
    }
    const typed = text.trim();
    const named = catalog().find((zone) => zone.id.toLowerCase() === typed.toLowerCase());
    const matches =
      typed === "" ? [] : searchZones(catalog(), typed, { words: (id) => zoneWords(id, now) });
    const [only] = matches;
    // An emptied box clears the zone. An id in the list is taken in the list's spelling, and one
    // the runtime accepts and the list does not carry is the rep's to type, kept as typed. One
    // match, or a city typed whole, is what the rep meant. Several is a choice left unmade.
    const meant =
      typed === ""
        ? ""
        : (named?.id ??
          (isTimeZone(typed)
            ? typed
            : only && (matches.length === 1 || only.words.city === fold(typed))
              ? only.id
              : null));
    if (meant !== null) {
      choose(meant);
      return;
    }
    setMissed(
      matches.length === 0
        ? `No time zone matches "${typed}". Try a city or a country.`
        : `Several time zones match "${typed}". Choose one from the list.`,
    );
    putBack();
  };

  const keys = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (disabled) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setAt(0);
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setAt((index) => Math.min(Math.max(index + step, 0), Math.max(rows.length - 1, 0)));
    } else if (event.key === "Enter" && open) {
      // An open list owns Enter: it must never submit the form around a half-typed query.
      event.preventDefault();
      if (current) choose(current.zone.id);
    } else if (event.key === "Tab" && open && searching && current) {
      choose(current.zone.id);
    } else if (event.key === "Escape" && (open || searching)) {
      event.preventDefault();
      event.stopPropagation();
      putBack();
    }
  };

  const known = value !== "" && isTimeZone(value);
  const place = known ? describe(value) : null;
  const clock = known ? clockOf(value, now) : null;
  const where =
    place && clock
      ? [
          [place.city, place.country].filter(Boolean).join(", "),
          nameOf(value, now),
          clock.offset,
          `${clock.time} now`,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;
  const unusable =
    value !== "" && !known && !searching
      ? `"${value}" is not a time zone the app can use. Choose one from the list.`
      : null;
  // Held for the length of a press: a zone taken as the box loses focus must not move the button
  // the rep is pressing. The form already has the zone; only the lines under the box wait.
  const note = useHeldWhilePressed(where);
  const problem = useHeldWhilePressed(unusable ?? (searching ? null : missed));
  const described = [props.describedBy, note ? noteId : null, problem ? missedId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="lcr-entry lcr-zone" ref={root}>
      <div
        className="lcr-entry-box"
        data-invalid={unusable ? "" : undefined}
        data-disabled={disabled ? "" : undefined}
      >
        <input
          ref={input}
          id={props.id}
          type="text"
          role="combobox"
          className="lcr-entry-input"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && current ? optionId(at) : undefined}
          aria-invalid={unusable ? true : undefined}
          aria-describedby={described || undefined}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={props.placeholder ?? "City, country or zone"}
          disabled={disabled}
          required={props.required}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setSearching(true);
            setOpen(true);
            setAt(0);
            setMissed(null);
          }}
          // Typing replaces the zone rather than editing its id letter by letter.
          onFocus={(event) => event.target.select()}
          onBlur={settle}
          onKeyDown={keys}
        />
        <button
          type="button"
          className="lcr-entry-pick"
          aria-label="Show time zones"
          tabIndex={-1}
          disabled={disabled}
          // The box keeps focus: the list is driven from it.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            input.current?.focus();
            setOpen((shown) => !shown);
            setAt(0);
          }}
        >
          <ChevronDownIcon size={16} />
        </button>
      </div>
      {note && (
        <p className="lcr-entry-note" id={noteId}>
          {note}
        </p>
      )}
      {problem && (
        <p
          className={problem === unusable ? "lcr-entry-problem" : "lcr-entry-note"}
          id={missedId}
          role={problem === unusable ? "alert" : "status"}
        >
          {problem}
        </p>
      )}
      {open && (
        // A press in the list must not take focus out of the box that drives it.
        <div
          ref={list}
          id={listId}
          role="listbox"
          aria-label="Time zones"
          className="lcr-popover lcr-entry-popover lcr-zone-list"
          data-end={edge.end ? "" : undefined}
          data-above={edge.above ? "" : undefined}
          onMouseDown={(event) => event.preventDefault()}
        >
          {rows.length === 0 && (
            <p className="lcr-zone-empty">
              No time zone matches “{query}”. Try a city or a country.
            </p>
          )}
          {rows.map((row, index) => {
            const zoneNow = clockOf(row.zone.id, now);
            const detail = [row.why, row.zone.country, nameOf(row.zone.id, now)]
              .filter(Boolean)
              .join(" · ");
            return (
              <div key={`${row.group}:${row.zone.id}`} role="presentation">
                {row.heading && <p className="lcr-label lcr-zone-heading">{row.heading}</p>}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: the keys are handled on the box, which keeps focus (aria-activedescendant) */}
                <div
                  id={optionId(index)}
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === at}
                  data-zone={row.zone.id}
                  className="lcr-zone-option"
                  onClick={() => choose(row.zone.id)}
                  onMouseMove={() => {
                    if (index !== at) setAt(index);
                  }}
                >
                  <span className="lcr-zone-place">
                    <span className="lcr-zone-city">{row.zone.city}</span>
                    {detail && <span className="lcr-zone-detail">{detail}</span>}
                  </span>
                  {zoneNow && (
                    <span className="lcr-zone-clock">
                      <span className="lcr-zone-offset">{zoneNow.offset}</span>
                      <span className="lcr-zone-time">{zoneNow.time}</span>
                    </span>
                  )}
                  <span className="lcr-zone-check">
                    {row.zone.id === value && (
                      <>
                        <CheckIcon size={16} />
                        <span className="lcr-visually-hidden">chosen</span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
